import { useEffect, useMemo, useState } from "react"
import { Async, Column, FieldType, FieldValue, Filters, Section, Table } from "../../../../../features/ui"
import { ProductName, useProductLoaders } from "../../../../products"
import { ProductYield } from "../../../state/types"

type Props = {
    items: Partial<ProductYield>[]
    readOnly?: boolean
    onCreateItem?: () => void
    onRemoveItem?: (productId: string) => void
}

export const ExcludedProductsTable = (props: Props) => {
    const { items, readOnly, onCreateItem, onRemoveItem } = props

    const productLoaders = useProductLoaders()
    
    const [productNames, setProductNames] = useState<Record<string, string>>({})
    const [searchTerm, setSearchTerm] = useState("")

    useEffect(() => {
        const missingIds = items
            .map(i => i.productId!)
            .filter(id => id && !productNames[id])
        if (missingIds.length === 0) return
        productLoaders().loadProducts(missingIds).then(products => {
            const updates: Record<string, string> = {}
            products.forEach(p => { if (p.name) updates[p.id] = p.name })
            setProductNames(prev => ({ ...prev, ...updates }))
        })
    }, [items])

    const columns: Column[] = [
        {
            name: 'productId',
            type: FieldType.TEXT,
            label: 'Producto',
            render: (value?: FieldValue) => (
                <Async
                    loadFn={async () => {
                        const product = await productLoaders().loadProduct(value! as string)
                        return <ProductName product={product} />
                    }}
                />
            )
        }
    ]

    const rows = useMemo(() => {
        const data = items.map(item => ({
            ...item,
            _sortName: productNames[item.productId!]?.toLowerCase() || '',
            _searchName: (productNames[item.productId!]?.toLowerCase() || '')
        }))
        
        const filteredData = searchTerm 
            ? data.filter(row => row._searchName.includes((searchTerm)))
            : data
        
        return filteredData.sort((a, b) => a._sortName.localeCompare(b._sortName))
    }, [items, productNames, searchTerm])

    return (
        <Section 
            id='excludedProducts' 
            title='Productos Excluidos' 
            space={12}
            collapsable={readOnly}
            defaultExpanded
        >
            <Filters
                filtersKey="excluded-products-search"
                searchField={{
                    name: 'search',
                    type: FieldType.TEXT,
                    placeholder: 'Buscar en excluidos...',
                    icon: 'search',
                    value: searchTerm,
                    optional: true,
                    onChange: (val) => setSearchTerm(val as string)
                }}
                fields={undefined}
            />
            <Table
                columns={columns}
                rows={rows}
                rowId='productId'
                readOnly={readOnly}
                onCreate={onCreateItem}
                onRemove={onRemoveItem}
                pageSize={5}
            />
        </Section>
    )
}